import type { Scorecard } from '@compass/client/types/scorecard';
import { Code2, ListChecks } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { JsonEditor, type JsonEditorMeta } from '@/components/ui/json-editor';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

import {
  deriveSelections,
  type FindingSelection,
  parseQcOutput,
  type QcFinding,
  serializeQcOutput,
} from '../langfuseFindings';

// Scorecard ScoringType: 2 = SCALE (Yes/Partial/No); everything else is binary.
const SCORING_TYPE_SCALE = 2;

interface AnswerOption {
  label: string;
  value: number;
}
const BINARY_OPTIONS: AnswerOption[] = [
  { label: 'Fail', value: 0 },
  { label: 'Pass', value: 1 },
];
const SCALE_OPTIONS: AnswerOption[] = [
  { label: 'No', value: 0 },
  { label: 'Partial', value: 0.5 },
  { label: 'Yes', value: 1 },
];

interface QuestionMeta {
  title: string;
  sectionTitle: string;
  sectionIndex: number;
  scale: boolean;
}

const humanizeMetricKey = (key: string | undefined): string => {
  if (!key) return 'Untitled';
  const parts = key.split('.');
  const last = (parts[parts.length - 1] || key).replace(/_/g, ' ');
  return last.charAt(0).toUpperCase() + last.slice(1);
};

const evidenceSnippet = (finding: QcFinding): string | null => {
  const ev = finding.evidence as { snippet?: string } | undefined;
  return typeof ev?.snippet === 'string' ? ev.snippet : null;
};

interface FindingsEditorProps {
  /** The trace's full output — the candidate pool of findings. */
  poolOutput: unknown;
  /** Current expected-output JSON string (seed / current selection). */
  value: string;
  onChange: (value: string, meta: JsonEditorMeta) => void;
  scorecard: Scorecard | null;
  className?: string;
}

/**
 * Structured editor for a call_qc expected output. Lets the reviewer pick which
 * findings to include and change each answer (Pass/Fail or Yes/Partial/No),
 * grouped by scorecard section. Falls back to a raw JSON editor when the output
 * isn't structured findings, or when the user flips the "raw JSON" toggle.
 */
export const FindingsEditor: React.FC<FindingsEditorProps> = ({
  poolOutput,
  value,
  onChange,
  scorecard,
  className,
}) => {
  const [raw, setRaw] = useState(false);

  const pool = useMemo(() => parseQcOutput(poolOutput), [poolOutput]);

  const questionMeta = useMemo(() => {
    const map = new Map<string, QuestionMeta>();
    (scorecard?.sections ?? []).forEach((section, sectionIndex) => {
      (section.questions ?? []).forEach(q => {
        if (q.metric_key && !map.has(q.metric_key)) {
          map.set(q.metric_key, {
            title: q.title || q.label || humanizeMetricKey(q.metric_key),
            sectionTitle: section.title?.trim() || `Section ${sectionIndex + 1}`,
            sectionIndex,
            scale: q.scoring_type === SCORING_TYPE_SCALE,
          });
        }
      });
    });
    return map;
  }, [scorecard]);

  const selections = useMemo<FindingSelection[]>(() => {
    if (!pool) return [];
    return deriveSelections(pool, parseQcOutput(value) ?? { findings: [] });
  }, [pool, value]);

  const poolFindings = pool?.findings ?? [];

  // Group candidates (pool findings, by index) into scorecard sections.
  const groups = useMemo(() => {
    const bySection = new Map<string, { order: number; title: string; indices: number[] }>();
    poolFindings.forEach((f, i) => {
      const meta = f.metric_key ? questionMeta.get(f.metric_key) : undefined;
      const title = meta?.sectionTitle ?? 'Other';
      const order = meta?.sectionIndex ?? Number.MAX_SAFE_INTEGER;
      const g = bySection.get(title) ?? { order, title, indices: [] };
      g.indices.push(i);
      bySection.set(title, g);
    });
    return [...bySection.values()].sort((a, b) => a.order - b.order);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pool, questionMeta]);

  const emit = (next: FindingSelection[]) => {
    if (!pool) return;
    onChange(JSON.stringify(serializeQcOutput(pool, next), null, 2), { valid: true });
  };

  // Fall back to the raw editor when there's nothing structured to show.
  if (!pool || raw) {
    return (
      <div className={cn('flex min-h-0 flex-col gap-1.5', className)}>
        {pool && (
          <div className="flex shrink-0 justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1.5 text-xs"
              onClick={() => setRaw(false)}
            >
              <ListChecks className="h-3.5 w-3.5" />
              Structured
            </Button>
          </div>
        )}
        <JsonEditor
          value={value}
          fill
          className="min-h-0 flex-1"
          aria-label="Expected output JSON"
          onChange={onChange}
        />
      </div>
    );
  }

  const includedCount = selections.filter(s => s.included).length;

  const setIncluded = (index: number, included: boolean) => {
    const next = selections.map((s, i) => (i === index ? { ...s, included } : s));
    emit(next);
  };
  const setAnswer = (index: number, rawScore: number) => {
    const next = selections.map((s, i) => (i === index ? { included: true, rawScore } : s));
    emit(next);
  };

  return (
    <div className={cn('flex min-h-0 flex-col gap-2', className)}>
      <div className="flex shrink-0 items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {includedCount} of {poolFindings.length} findings selected
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => emit(selections.map(s => ({ ...s, included: true })))}
          >
            Add all
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => emit(selections.map(s => ({ ...s, included: false })))}
          >
            Clear
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-xs"
            onClick={() => setRaw(true)}
          >
            <Code2 className="h-3.5 w-3.5" />
            Raw JSON
          </Button>
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-4 pr-3">
          {groups.map(group => (
            <div key={group.title} className="space-y-1.5">
              <div className="px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.title}
              </div>
              {group.indices.map(i => {
                const finding = poolFindings[i];
                const sel = selections[i];
                const meta = finding.metric_key ? questionMeta.get(finding.metric_key) : undefined;
                const options = meta?.scale ? SCALE_OPTIONS : BINARY_OPTIONS;
                const snippet = evidenceSnippet(finding);
                return (
                  <div
                    key={i}
                    className={cn(
                      'rounded-md border px-3 py-2',
                      sel.included
                        ? 'border-border bg-background'
                        : 'border-transparent opacity-60',
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <label className="flex min-w-0 items-start gap-2">
                        <Checkbox
                          checked={sel.included}
                          onCheckedChange={c => setIncluded(i, c === true)}
                          className="mt-0.5"
                          aria-label={`Include ${meta?.title ?? finding.metric_key}`}
                        />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-foreground">
                            {meta?.title ?? humanizeMetricKey(finding.metric_key)}
                          </span>
                          <span className="block truncate text-[11px] text-muted-foreground">
                            {finding.metric_key}
                          </span>
                        </span>
                      </label>
                      <div className="flex shrink-0 overflow-hidden rounded-md border">
                        {options.map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setAnswer(i, opt.value)}
                            className={cn(
                              'px-2 py-1 text-xs transition-colors',
                              sel.rawScore === opt.value
                                ? opt.value >= 1
                                  ? 'bg-positive text-positive-foreground'
                                  : opt.value <= 0
                                    ? 'bg-critical text-critical-foreground'
                                    : 'bg-attention text-attention-foreground'
                                : 'text-muted-foreground hover:bg-muted',
                            )}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {snippet && (
                      <p className="mt-1.5 line-clamp-2 pl-6 text-xs text-muted-foreground">
                        {snippet}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
